import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Award, Trophy, Star, Target } from 'lucide-react';
import { useTheme } from '../hooks/useTheme';

const Achievements: React.FC = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [currentSlide, setCurrentSlide] = useState(0);

  const achievements = [
    {
      icon: Award,
      title: "Tanzania Digital Awards Founder",
      description: "Established national platform celebrating digital innovation and transformation leaders across Tanzania.",
      category: "Innovation",
      year: "2020"
    },
    {
      icon: Trophy,
      title: "BBT-YIA Campaign Success",
      description: "Led communication strategy that rebranded agriculture for youth, resulting in 15,000+ youth-led agribusinesses.",
      category: "Impact",
      year: "2023"
    },
    {
      icon: Star,
      title: "DART Social Media Revamp",
      description: "Increased public engagement by 300% for Tanzania's premier urban transport system through strategic digital communication.",
      category: "Digital",
      year: "2019"
    },
    {
      icon: Target,
      title: "250+ Campaigns Executed",
      description: "Successfully designed and executed communication strategies enhancing client impact in sustainable development.",
      category: "Excellence",
      year: "2023"
    },
    {
      icon: Award,
      title: "110+ Client Partnerships",
      description: "Established strategic partnerships with clients and media outlets across government, private sector, and NGOs.",
      category: "Partnerships",
      year: "2023"
    },
    {
      icon: Trophy,
      title: "Youth Empowerment Leader",
      description: "Mentored and trained thousands of youth in business skills, advocacy, and digital communication across Tanzania.",
      category: "Leadership",
      year: "2023"
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % Math.ceil(achievements.length / 3));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + Math.ceil(achievements.length / 3)) % Math.ceil(achievements.length / 3));
  };

  const getCurrentAchievements = () => {
    const startIndex = currentSlide * 3;
    return achievements.slice(startIndex, startIndex + 3);
  };

  return (
    <section id="achievements" className="py-20 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fade-in-up">
          <h2 className={`text-4xl md:text-5xl font-light mb-6 ${
            isDark ? 'text-white' : 'text-charcoal'
          }`}>
            Key <span className="gradient-text font-medium">Achievements</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-sunrise-orange to-serengeti-gold mx-auto rounded-full mb-4"></div>
          <p className={`text-lg max-w-2xl mx-auto ${
            isDark ? 'text-gray-400' : 'text-gray-600'
          }`}>
            Recognized excellence in leadership, innovation, and business transformation
          </p>
        </div>

        {/* Achievements Carousel */}
        <div className="relative">
          {/* Navigation Buttons */}
          <button
            onClick={prevSlide}
            className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 p-3 rounded-full glass border transition-all duration-300 hover:scale-110 ${
              isDark 
                ? 'glass-dark text-white hover:bg-white/10 border-white/20' 
                : 'glass-light text-charcoal hover:bg-white/60 border-gray-300/50'
            }`}
          >
            <ChevronLeft size={24} />
          </button>

          <button
            onClick={nextSlide}
            className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 p-3 rounded-full glass border transition-all duration-300 hover:scale-110 ${
              isDark 
                ? 'glass-dark text-white hover:bg-white/10 border-white/20' 
                : 'glass-light text-charcoal hover:bg-white/60 border-gray-300/50'
            }`}
          >
            <ChevronRight size={24} />
          </button>

          {/* Achievements Grid */}
          <div className="grid md:grid-cols-3 gap-8 px-8">
            {getCurrentAchievements().map((achievement, index) => (
              <div 
                key={achievement.title}
                className={`glass border rounded-3xl p-8 shadow-2xl transition-all duration-500 hover:scale-105 animate-fade-in-up ${
                  isDark ? 'glass-dark' : 'glass-light'
                }`}
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                <div className="text-center">
                  <div className={`w-20 h-20 mx-auto mb-6 rounded-2xl flex items-center justify-center ${
                    isDark ? 'bg-sunrise-orange/20' : 'bg-sunrise-orange/10'
                  }`}>
                    <achievement.icon size={32} className="text-sunrise-orange" />
                  </div>
                  
                  <div className={`inline-block px-3 py-1 rounded-full text-xs font-medium mb-4 ${
                    isDark 
                      ? 'bg-serengeti-gold/20 text-serengeti-gold border border-serengeti-gold/30' 
                      : 'bg-serengeti-gold/10 text-serengeti-gold border border-serengeti-gold/30'
                  }`}>
                    {achievement.category} • {achievement.year}
                  </div>
                  
                  <h3 className={`text-xl font-semibold mb-4 ${
                    isDark ? 'text-white' : 'text-charcoal'
                  }`}>
                    {achievement.title}
                  </h3>
                  
                  <p className={`leading-relaxed ${
                    isDark ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    {achievement.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Dots Indicator */}
          <div className="flex justify-center mt-12 gap-3">
            {Array.from({ length: Math.ceil(achievements.length / 3) }).map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentSlide 
                    ? 'bg-sunrise-orange scale-125' 
                    : isDark ? 'bg-white/30 hover:bg-white/50' : 'bg-gray-400 hover:bg-gray-600'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Stats Section */}
        <div className="mt-20">
          <div className={`glass border rounded-3xl p-8 lg:p-12 shadow-2xl ${
            isDark ? 'glass-dark' : 'glass-light'
          }`}>
            <div className="text-center mb-12">
              <h3 className={`text-2xl font-medium mb-4 ${
                isDark ? 'text-white' : 'text-charcoal'
              }`}>
                Impact by Numbers
              </h3>
              <div className="w-16 h-1 bg-gradient-to-r from-sunrise-orange to-serengeti-gold mx-auto rounded-full"></div>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-4xl font-bold gradient-text mb-2">10+</div>
                <div className={`text-sm font-medium ${
                  isDark ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  Years Experience
                </div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold gradient-text mb-2">250+</div>
                <div className={`text-sm font-medium ${
                  isDark ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  Campaigns Led
                </div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold gradient-text mb-2">15K+</div>
                <div className={`text-sm font-medium ${
                  isDark ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  Youth Empowered
                </div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold gradient-text mb-2">110+</div>
                <div className={`text-sm font-medium ${
                  isDark ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  Client Partnerships
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Achievements;
